import { WebSocketConnection } from 'Common/utils/webSocketConnection'

interface WebSocketCacheEntry {
  webSocketConnection: WebSocketConnection | undefined
  activeSubscriptions: number
}

const webSocketCache = new Map<string, WebSocketCacheEntry>()

class WebSocketConnectionCache {
  /**
   * If websocket does not exist or is closed, opens websocket connection as defined in the provided WebSocketConnection object. Otherwise the WebSocketCache entry active subscriptions is incremented.
   * @param key Key to store and access websocketconnection entry within WebSocketCache
   * @param webSocketConnection WebSocketConnection Object
   */
  subscribe(key: string, webSocketConnection: WebSocketConnection) {
    const entry = this.getCacheEntry(key)
    const isWebSocketConnectionDefined = entry?.webSocketConnection === undefined
    const isWebSocketClosing = entry?.webSocketConnection?.websocket?.readyState === WebSocket.CLOSING
    const isWebSocketClosed = entry?.webSocketConnection?.websocket?.readyState === WebSocket.CLOSED

    const shouldCreateEntry =
      entry === undefined || isWebSocketConnectionDefined || isWebSocketClosing || isWebSocketClosed

    if (shouldCreateEntry) {
      const newEntry = this.createEntryObj(webSocketConnection)
      const currentOnOpenCallback = webSocketConnection.onopen
      const currentOnCloseCallback = webSocketConnection.onclose

      webSocketCache.set(key, newEntry)

      //TODO: ENHANCEMENT -
      //onError - better report that the underlying websocket connection
      //failed to establish up through the cache
      webSocketConnection.onopen = (e) => {
        newEntry.activeSubscriptions++
        if (currentOnOpenCallback && typeof currentOnOpenCallback === 'function') {
          currentOnOpenCallback(e)
        }
      }

      webSocketConnection.onclose = (e) => {
        this.removeCacheEntry(key)
        if (currentOnCloseCallback && typeof currentOnCloseCallback === 'function') {
          currentOnCloseCallback(e)
        }
      }

      webSocketConnection.create()
    } else entry.activeSubscriptions++
  }

  /**
   * Decrements the number of active subscribers for a websocket connection. If the number of active subscribers falls below zero the associated websocket connection will be closed and the entry removed from the WebSocketCache.
   * @param key
   */
  unsubscribe(key: string) {
    const entry = this.getCacheEntry(key)
    if (entry) {
      const webSocket = entry?.webSocketConnection?.websocket
      entry.activeSubscriptions--
      if (entry.activeSubscriptions === 0) {
        if (webSocket && !webSocket.CLOSED && !webSocket.CLOSING) {
          webSocket.close()
        }
        this.removeCacheEntry(key)
      }
    }
  }

  /**
   * Creates a base WebSocketEntry object to be stored in WebSocketCache.
   * @param webSocketConnection WebSocketConnection object to be stored within the entry
   * @returns WebSocketCacheEntry object to be stored in WebSocketCache
   */
  createEntryObj(webSocketConnection?: WebSocketConnection): WebSocketCacheEntry {
    return {
      webSocketConnection: webSocketConnection,
      activeSubscriptions: 0,
    }
  }

  /**
   * Retrieves WebSocketCacheEntry object from websocket cache
   * @param key Identifier for WebSocketCacheEntry within WebSocketCache.
   * @returns WebSocketCacheEntry object
   */
  getCacheEntry(key: string): WebSocketCacheEntry | undefined {
    return webSocketCache.get(key)
  }

  /**
   * Removes WebSocketCacheEntry Object from websocket cache
   * @param key Identifier for WebSocketCacheEntry within WebSocketCache.
   */
  removeCacheEntry(key: string) {
    webSocketCache.delete(key)
  }
}

export { WebSocketConnectionCache }
